import { CardPicasso, Section, SectionHeader } from "@liberetech/design-system";
import Button from "@liberetech/design-system/Button";
import HeroSplit, { imageSizes } from "@liberetech/design-system/HeroSplit";
import NavLink from "@liberetech/design-system/NavLink";
import Prose from "@liberetech/design-system/Prose";
import classNames from "classnames";
import { useEffect, useState } from "react";

import { Page } from "components/Context/PageContext";
import { useTrackingContext } from "components/Context/TrackingContext";
import ImageCDN from "components/ImageCDN/ImageCDN";
import { ArticleJsonLd, LanguageAlternates, Seo } from "components/Seo/Seo";
import { AssetType } from "gql/types";
import fetchCms from "lib/client/cms";
import { useLanguage, useTranslation } from "lib/i18n/client";
import { useTranslation as useTranslationServer } from "lib/i18n/server";
import { assetPath } from "lib/routes/paths";
import { TrackingContextPage } from "lib/tracking";
import withDirectChannelCommonProps from "lib/withDirectChannelCommonProps";
import { DirectChannelCommonProps } from "types";

import styles from "./[slug].module.css";

const BlogPost: React.FC<BlogPostProps & DirectChannelCommonProps> = ({
  post,
  brand,
  expBlogConversion,
}) => {
  const { t } = useTranslation("blog");

  return (
    <>
      <Page title={post.title} backHref="/blog" />
      <Seo
        title={`${post.title} | Blog`}
        description={post.description ?? undefined}
        openGraph={{ images: [{ url: post.thumbnail?.src || "" }] }}
      />
      <HeroSplit
        title={post.title}
        image={
          <ImageCDN
            src={post.thumbnail?.src || ""}
            sizes={imageSizes}
            alt={post.title}
          />
        }
      />
      <div
        className={classNames("grid", {
          [styles.withExperimentA]: expBlogConversion === "A",
          [styles.withExperimentB]: expBlogConversion === "B",
        })}
      >
        <div className={styles.post}>
          <Prose>
            <div dangerouslySetInnerHTML={{ __html: post.content }} />
          </Prose>
        </div>
        {expBlogConversion && <AssetCard variant={expBlogConversion} />}
      </div>
      {post.relatedPosts.length > 0 && (
        <Section background="secondary">
          <div className="grid">
            <SectionHeader
              label={brand.name}
              title={t("relatedPosts")}
              centered
            />
            <ul className={classNames(styles.relatedPosts, "grid")}>
              {post.relatedPosts.map((post, i) => (
                <li className={styles.relatedPost} key={i}>
                  <NavLink href={post.uri}>
                    <CardPicasso
                      elevation={1}
                      key={i}
                      title={post.title}
                      image={
                        <ImageCDN
                          alt={post.title}
                          src={post.featuredImage.node.sourceUrl}
                          sizes="400px"
                          loading="lazy"
                        />
                      }
                    />
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        </Section>
      )}
      <ArticleJsonLd
        title={post.title}
        description={post.description ?? ""}
        images={[post.seo.opengraphImage?.src || post.thumbnail?.src || ""]}
        datePublished={post.createdAt}
        dateModified={post.modifiedAt}
        authorName={brand.name}
        publisherName={brand.name}
        publisherLogo=""
        url=""
      />
    </>
  );
};

const AssetCard: React.FC<{ variant: "A" | "B" }> = ({ variant }) => {
  const { t } = useTranslation();
  const lang = useLanguage();
  const { trackImpression } = useTrackingContext();
  const [isVisible, setVisible] = useState<boolean>(true);
  useEffect(() => {
    trackImpression({ category: "blog_banner", label: `variant_${variant}` });
  }, []);

  if (!isVisible) {
    return null;
  }

  return (
    <div className={styles.assetCard}>
      <NavLink
        href={assetPath(t, lang, {
          assetType: AssetType.Aparthotel,
          slug: "libere-vitoria",
        })}
        className={styles.assetCardWrap}
        onClick={() =>
          window.dataLayer.push({
            event: "click",
            event_category: "blog_banner",
            event_label: `variant_${variant}`,
          })
        }
      >
        <img
          className={styles.assetCardImage}
          src="/static/images/blog/blog-banner-vitoria.jpg"
        />
        <div className={styles.assetCardContent}>
          <p className={styles.assetCardTitle}>
            Disfruta de tu estancia en Vitoria en el apartahotel Líbere
          </p>
          <p>
            Situados a escasos minutos del centro el apartahotel Líbere Vitoria
            te permitirá disfrutar de la ciudad con total libertad.
          </p>
          <Button className={styles.assetCardButton}>Más información</Button>
        </div>
      </NavLink>
      {variant === "B" && (
        <button
          className={styles.assetCardClose}
          onClick={() => {
            window.dataLayer.push({
              event: "close",
              event_category: "blog_banner",
              event_label: `variant_${variant}`,
            });
            setVisible(false);
          }}
        >
          <div className={styles.assetCardCloseIcon}>
            <svg viewBox="0 0 24 24">
              <path d="M16.5 7.5l-9 9M16.5 16.5l-9-9" />
            </svg>
          </div>
        </button>
      )}
    </div>
  );
};

export const getServerSideProps = withDirectChannelCommonProps<
  BlogPostProps,
  BlogPostParams
>(async ({ req, params, locale }) => {
  if (
    !params ||
    !params.slug ||
    Array.isArray(params.slug) ||
    !locale ||
    !req.context.brand?.enabledFeatures?.includes("blog")
  ) {
    return {
      notFound: true,
    };
  }

  const response = await fetchCms(
    `{
      post(id: "/${locale}/blog/${params.slug}" idType: SLUG) {
        title
        slug
        excerpt
        content
        date
        modified
        uri
        featuredImage {
          node {
            sourceUrl
            srcSet
          }
        }
        seo {
          metaDesc
          opengraphImage {
            sourceUrl
          }  
        }
        translations {
          slug
          language {
            slug
          }
        }
        relatedPosts {
          relatedPosts {
            ... on Post {
              title
              uri
              featuredImage {
                node {
                  sourceUrl
                }
              }
            }
          }
        }
      }
    }`,
    req.context.brand.cmsUrl,
  );

  if (!response || !response.post) {
    return {
      notFound: true,
    };
  }

  const post = response.post as {
    title: string;
    slug: string;
    excerpt: string;
    content: string;
    date: string;
    modified: string;
    uri: string;
    featuredImage?: { node: { sourceUrl: string; srcSet: string } };
    seo: { metaDesc?: string; opengraphImage: { sourceUrl: string } };
    translations: Array<{ slug: string; language: { slug: string } }>;
    relatedPosts: {
      relatedPosts: Array<{
        title: string;
        uri: string;
        featuredImage: {
          node: {
            sourceUrl: string;
          };
        };
      }>;
    };
  };
  const t = await useTranslationServer(locale, "common");

  return {
    props: {
      post: {
        title: post.title,
        description: post.seo.metaDesc || post.excerpt,
        slug: post.slug,
        content: post.content,
        createdAt: post.date,
        modifiedAt: post.modified,
        thumbnail: post.featuredImage
          ? {
              src: post.featuredImage.node.sourceUrl,
              srcSet: post.featuredImage.node.srcSet,
            }
          : undefined,
        uri: post.uri,
        seo: {
          opengraphImage: {
            src: post.seo.opengraphImage.sourceUrl,
          },
        },
        relatedPosts: post.relatedPosts.relatedPosts || [],
      },
      trackingContext: {
        page_type: "blog_post",
      },
      expBlogConversion:
        post.slug === "donde-comer-en-vitoria" &&
        req.context.brand.enabledFeatures?.includes("exp-blog-conversion")
          ? Math.round(Math.random())
            ? "A"
            : "B"
          : null,
      languageAlternates: [
        {
          hrefLang: locale,
          href: `/${locale}/blog/${post.slug}`,
        },
        ...post.translations.reduce((prev: LanguageAlternates, translation) => {
          if (!translation.slug || !translation.language.slug) {
            return prev;
          }
          const lang = translation.language.slug;
          return [
            ...prev,
            { hrefLang: lang, href: `/${lang}/blog/${translation.slug}` },
          ];
        }, []),
      ],
      breadcrumbs: [
        { name: t("common:breadcrumbs.home"), item: `/${locale}` },
        { name: "Blog", item: `/${locale}/blog` },
        { name: post.title, item: `${post.uri}` },
      ],
    },
  };
});

type BlogPostProps = {
  post: {
    title: string;
    description: string;
    slug: string;
    content: string;
    createdAt: string;
    modifiedAt: string;
    uri: string;
    thumbnail?: { src: string; srcSet: string };
    seo: {
      opengraphImage?: { src?: string };
    };
    relatedPosts: Array<{
      title: string;
      uri: string;
      featuredImage: {
        node: {
          sourceUrl: string;
        };
      };
    }>;
  };
  expBlogConversion: "A" | "B" | null;
  trackingContext: TrackingContextPage;
};

type BlogPostParams = {
  slug: string;
};

export default BlogPost;
